import { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Col, Input, Row, Spin } from "antd";
import "./PromptChoices.css";

export default function PromptChoices({ items = [], onChangeSelected, onRevisePrompt, onEditPrompt, revisingKey, editingKey }) {
  const [values, setValues] = useState([]);
  const [modes, setModes] = useState({});
  const [editedItems, setEditedItems] = useState({});
  const [reviseTextByKey, setReviseTextByKey] = useState({});

  const keyOf = (it) => String(it?.id ?? it?.title ?? "");

  const normalizedItems = useMemo(() => {
    return (items ?? []).map((it) => {
      const key = keyOf(it);
      return {
        ...it,
        _key: key,
        _description: it?.description ?? it?.detailed_prompt ?? it?.detailedPrompt ?? "",
      };
    });
  }, [items]);

  useEffect(() => {
    const valid = new Set(normalizedItems.map((x) => x._key));
    setValues((prev) => prev.filter((v) => valid.has(String(v))));
  }, [normalizedItems]);

  const handleChange = (next) => {
    setValues(next);
    onChangeSelected?.(next);
  };

  const setMode = (key, mode) => setModes((prev) => ({ ...prev, [key]: mode }));

  const doneMode = (key) => {
    setModes((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    setEditedItems((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    setReviseTextByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };


  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEditChange = (key, field, value) => {
    setEditedItems((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const handleRevise = (key) => (e) => {
    stop(e);
    setMode(key, "revise");
  };

  const handleEdit = (e, it) => {
    stop(e);
    const key = it._key;

    setEditedItems((prev) => ({
      ...prev,
      [key]: {
        title: it.title ?? "",
        description: it._description ?? "",
      },
    }));

    setMode(key, "edit");
  };

  const handleDone = (key) => (e) => {
    stop(e);
    doneMode(key);
  };

  const handleTitleChange = (key) => (e) => {
    handleEditChange(key, "title", e.target.value);
  };

  const handlePromptChange = (key) => (e) => {
    handleEditChange(key, "description", e.target.value);
  };

  const handleReviseTextChange = (key) => (e) => {
    setReviseTextByKey((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmitRevise = (key) => async (e) => {
    stop(e);
    const text = (reviseTextByKey[key] ?? "").trim();
    if (!text) return;

    const ok = await onRevisePrompt?.(key, text);
    if (ok) {
      setReviseTextByKey((prev) => ({ ...prev, [key]: "" }));
      doneMode(key); // auto close revise mode
    }
  };

  return (
    <div className="prompt-choices-parent-container">
      <div className="prompt-choices-subheading">
        Choose one or more description to generate
      </div>
      <Checkbox.Group value={values} onChange={handleChange} className="checkbox-container">
        <Row className="prompt-choices-container" gutter={[0, 8]}>
          {normalizedItems.map((it) => {
            const key = it._key;
            const checked = values.includes(key);
            const mode = modes[key];
            const isEditing = mode === "edit";
            const isRevising = revisingKey === key;
            const isSavingEdit = editingKey === key;
            const spinning = isRevising || isSavingEdit;

            const showTitle = isEditing ? editedItems[key]?.title : it.title;
            const showDesc = isEditing ? editedItems[key]?.description : it._description;

            return (
              <Col key={key} span={24}>
                <Card className={`pick-card ${checked ? "is-selected" : ""}`}>
                  <Spin spinning={spinning}>
                    <Checkbox
                      value={key}
                      className="card-checkbox"
                      disabled={isEditing || spinning}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="row-title">
                        <div>
                          <div className="pick-title">{showTitle}</div>
                        </div>

                        <div onClick={stop}>
                          {!mode ? (
                            <>
                              <Button type="link" onClick={handleRevise(key)} disabled={isRevising}>
                                Revise
                              </Button>
                              <Button type="link" onClick={(e) => handleEdit(e, it)} disabled={isRevising}>
                                Edit
                              </Button>
                            </>
                          ) : (
                            <Button type="link" onClick={handleDone(key)} disabled={isRevising}>
                              Close
                            </Button>
                          )}
                        </div>
                      </div>

                      {mode === "edit" ? (
                        <div className="edit-container" onClick={stop}>
                          <div style={{ marginBottom: 8 }}>
                            <label>Title</label>
                            <Input
                              value={editedItems[key]?.title}
                              onChange={handleTitleChange(key)}
                              disabled={spinning}
                            />
                          </div>

                          <div>
                            <label>Prompt</label>
                            <Input.TextArea
                              autoSize={{ minRows: 3, maxRows: 6 }}
                              value={editedItems[key]?.description}
                              onChange={handlePromptChange(key)}
                              disabled={spinning}
                            />
                          </div>

                          <div className="edit-actions">
                            <Button
                              type="primary"
                              loading={isSavingEdit}
                              disabled={spinning}
                              onClick={async (e) => {
                                stop(e);
                                const payload = {
                                  title: (editedItems[key]?.title ?? "").trim(),
                                  description: (editedItems[key]?.description ?? "").trim(),
                                };
                                if (!payload.title && !payload.description) return;

                                const ok = await onEditPrompt?.(key, payload);
                                if (ok) doneMode(key);
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="pick-detail">{showDesc}</div>
                      )}

                      {mode === "revise" && (
                        <div className="prompt-container" onClick={stop}>
                          <Input
                            placeholder="Revise instructions"
                            value={reviseTextByKey[key] ?? ""}
                            onChange={handleReviseTextChange(key)}
                            disabled={isRevising}
                          />
                          <Button onClick={handleSubmitRevise(key)} loading={isRevising} disabled={isRevising}>
                            Revise Prompt
                          </Button>
                        </div>
                      )}
                    </Checkbox>
                  </Spin>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Checkbox.Group>
    </div>
  );
}
